import React, { useEffect, useState } from 'react';
import { FlipHorizontal2, FlipVertical2, RotateCw } from 'lucide-react';
import { useEditorStore } from '../../store/editorStore';
import { useCanvasStore } from '../../store/canvasStore';
import { pixelsToMetres, metresToPixels, DEFAULT_WALL_THICKNESS } from '../../canvasConstants';
import { familyOf, symbolAnchorsToWall, symbolSupportsFlip, symbolSupportsStairDirection } from '../../symbols';
import { WallLinePicker } from '../Canvas/DrawAssist';
import { SymbolVariationRow } from './SymbolPanel';
import type { StairDirection } from '../../types';

const MIN_PX = 20;

interface PropertiesPanelProps {
  onCommit?: () => void;
}

const inputStyle: React.CSSProperties = {
  backgroundColor: '#252532',
  border: '1px solid #2e2e40',
  color: '#f0f0f5',
};

function wallLengthMetres(points: number[]): number {
  let total = 0;
  for (let i = 0; i + 3 < points.length; i += 2) {
    total += Math.hypot(points[i + 2] - points[i], points[i + 3] - points[i + 1]);
  }
  return pixelsToMetres(total);
}

const PropertiesPanel: React.FC<PropertiesPanelProps> = ({ onCommit }) => {
  const { selectedIds } = useEditorStore();
  const { shapes, updateShape, pushHistory } = useCanvasStore();
  const selected = shapes.find((s) => selectedIds.includes(s.id));

  const [label, setLabel] = useState('');
  const [width, setWidth] = useState('');
  const [height, setHeight] = useState('');

  useEffect(() => {
    if (!selected) return;
    setLabel(selected.label ?? '');
    setWidth(selected.width != null ? pixelsToMetres(selected.width).toFixed(2) : '');
    setHeight(selected.height != null ? pixelsToMetres(selected.height).toFixed(2) : '');
  }, [selected?.id]);

  if (!selected) {
    return (
      <div className="p-4 border-t" style={{ borderColor: '#2e2e40' }}>
        <h3 className="text-sm font-semibold mb-2" style={{ color: '#f0f0f5' }}>
          Properties
        </h3>
        <p className="text-xs" style={{ color: '#8888aa' }}>
          Select an element to edit its properties.
        </p>
      </div>
    );
  }

  const commit = () => {
    pushHistory();
    onCommit?.();
  };

  const handleLabelChange = (value: string) => {
    setLabel(value);
    updateShape(selected.id, { label: value });
  };

  const applyDimension = (axis: 'width' | 'height', raw: string) => {
    const metres = parseFloat(raw);
    if (!Number.isFinite(metres) || metres <= 0) {
      // Reset the field to the stored value.
      const current = selected[axis];
      const reset = current != null ? pixelsToMetres(current).toFixed(2) : '';
      if (axis === 'width') setWidth(reset);
      else setHeight(reset);
      return;
    }
    const px = Math.max(MIN_PX, metresToPixels(metres));
    updateShape(selected.id, { [axis]: px });
    commit();
  };

  const isRoom = selected.type === 'room' && selected.width != null && selected.height != null;
  const wallLen = selected.type !== 'room' && selected.points ? wallLengthMetres(selected.points) : null;
  const canRotate = selected.type === 'room' || selected.type === 'symbol';
  const stairDirection: StairDirection = selected.stairDirection === 'dn' ? 'dn' : 'up';
  const symbolFamily = selected.symbolId ? familyOf(selected.symbolId) : undefined;

  const rotateQuarter = () => {
    updateShape(selected.id, { rotation: ((selected.rotation ?? 0) + 90) % 360 });
    commit();
  };

  const setStairDirection = (direction: StairDirection) => {
    updateShape(selected.id, { stairDirection: direction });
    commit();
  };

  return (
    <div className="p-4 space-y-4 border-t" style={{ borderColor: '#2e2e40' }}>
      <h3 className="text-sm font-semibold" style={{ color: '#f0f0f5' }}>
        Properties
      </h3>

      <div>
        <label className="block text-xs mb-1" style={{ color: '#8888aa' }}>
          Label
        </label>
        <input
          type="text"
          value={label}
          onChange={(e) => handleLabelChange(e.target.value)}
          onBlur={commit}
          onKeyDown={(e) => {
            if (e.key === 'Enter') (e.target as HTMLInputElement).blur();
          }}
          placeholder="e.g. Bedroom 1"
          className="w-full px-3 py-2 rounded text-sm focus:outline-none focus:ring-1 focus:ring-[#FF4C4C]"
          style={inputStyle}
        />
      </div>

      {isRoom && (
        <div className="flex gap-2">
          <div className="flex-1">
            <label className="block text-xs mb-1" style={{ color: '#8888aa' }}>
              Width (m)
            </label>
            <input
              type="number"
              min={0.1}
              step={0.1}
              value={width}
              onChange={(e) => setWidth(e.target.value)}
              onBlur={(e) => applyDimension('width', e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') (e.target as HTMLInputElement).blur();
              }}
              className="w-full px-2 py-1.5 rounded text-sm focus:outline-none focus:ring-1 focus:ring-[#FF4C4C]"
              style={inputStyle}
            />
          </div>
          <div className="flex-1">
            <label className="block text-xs mb-1" style={{ color: '#8888aa' }}>
              Height (m)
            </label>
            <input
              type="number"
              min={0.1}
              step={0.1}
              value={height}
              onChange={(e) => setHeight(e.target.value)}
              onBlur={(e) => applyDimension('height', e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') (e.target as HTMLInputElement).blur();
              }}
              className="w-full px-2 py-1.5 rounded text-sm focus:outline-none focus:ring-1 focus:ring-[#FF4C4C]"
              style={inputStyle}
            />
          </div>
        </div>
      )}

      {isRoom && Number(width) > 0 && Number(height) > 0 && (
        <p className="text-xs" style={{ color: '#8888aa' }}>
          Area: <span style={{ color: '#f0f0f5' }}>{(Number(width) * Number(height)).toFixed(2)} m²</span>
        </p>
      )}

      {wallLen != null && (
        <p className="text-xs" style={{ color: '#8888aa' }}>
          Length: <span style={{ color: '#f0f0f5' }}>{wallLen.toFixed(2)} m</span>
        </p>
      )}

      {selected.symbolId && symbolFamily && (
        <SymbolVariationRow
          family={symbolFamily}
          activeId={selected.symbolId}
          onPick={(variation) => {
            const oldW = selected.width ?? 0;
            const oldH = selected.height ?? 0;
            const newW = metresToPixels(variation.widthM);
            const newH = metresToPixels(variation.heightM);
            const keepBaseline = symbolAnchorsToWall(selected.symbolId!) && symbolAnchorsToWall(variation.id);
            updateShape(selected.id, {
              symbolId: variation.id,
              width: newW,
              height: newH,
              x: selected.x + (oldW - newW) / 2,
              y: keepBaseline ? selected.y + (oldH - newH) : selected.y + (oldH - newH) / 2,
              ...(symbolSupportsStairDirection(variation.id) && !selected.stairDirection ? { stairDirection: 'up' as const } : {}),
            });
            commit();
          }}
        />
      )}

      {selected.symbolId && symbolSupportsStairDirection(selected.symbolId) && (
        <div>
          <p className="text-xs mb-1" style={{ color: '#8888aa' }}>
            Stair direction
          </p>
          <div className="flex gap-2">
            {(['up', 'dn'] as const).map((direction) => {
              const active = stairDirection === direction;
              return (
                <button
                  key={direction}
                  type="button"
                  onClick={() => setStairDirection(direction)}
                  className="flex-1 py-1.5 rounded text-xs font-semibold tracking-wide"
                  style={{
                    backgroundColor: active ? 'rgba(255,76,76,0.18)' : '#252532',
                    color: active ? '#FF4C4C' : '#f0f0f5',
                    border: `1px solid ${active ? '#FF4C4C' : '#2e2e40'}`,
                  }}
                >
                  {direction === 'up' ? 'UP' : 'DN'}
                </button>
              );
            })}
          </div>
        </div>
      )}

      {selected.type === 'wall' && (
        <div>
          <p className="text-xs mb-1" style={{ color: '#8888aa' }}>
            Line weight
          </p>
          <WallLinePicker
            thickness={selected.thickness ?? DEFAULT_WALL_THICKNESS}
            onChange={(px) => {
              updateShape(selected.id, { thickness: px });
              useEditorStore.getState().setWallThickness(px);
              commit();
            }}
          />
        </div>
      )}

      {selected.symbolId && symbolSupportsFlip(selected.symbolId) && (
        <div className="flex gap-2">
          <button
            type="button"
            onClick={() => {
              updateShape(selected.id, { flipX: !selected.flipX });
              commit();
            }}
            className="flex flex-1 items-center justify-center gap-1.5 py-2 rounded text-xs font-medium"
            style={{
              backgroundColor: selected.flipX ? 'rgba(255,76,76,0.18)' : '#252532',
              color: selected.flipX ? '#FF4C4C' : '#f0f0f5',
              border: `1px solid ${selected.flipX ? '#FF4C4C' : '#2e2e40'}`,
            }}
          >
            <FlipHorizontal2 size={14} />
            Mirror
          </button>
          <button
            type="button"
            onClick={() => {
              updateShape(selected.id, { flipY: !selected.flipY });
              commit();
            }}
            className="flex flex-1 items-center justify-center gap-1.5 py-2 rounded text-xs font-medium"
            style={{
              backgroundColor: selected.flipY ? 'rgba(255,76,76,0.18)' : '#252532',
              color: selected.flipY ? '#FF4C4C' : '#f0f0f5',
              border: `1px solid ${selected.flipY ? '#FF4C4C' : '#2e2e40'}`,
            }}
          >
            <FlipVertical2 size={14} />
            Flip
          </button>
        </div>
      )}

      {selected.type === 'symbol' && (
        <p className="text-[11px] leading-snug" style={{ color: '#8888aa' }}>
          Drag to move. Ctrl+C and Ctrl+V to copy.
        </p>
      )}

      {canRotate && (
        <button
          type="button"
          onClick={rotateQuarter}
          className="flex items-center justify-center gap-2 w-full py-2 rounded text-xs font-medium"
          style={{ backgroundColor: '#252532', color: '#f0f0f5', border: '1px solid #2e2e40' }}
        >
          <RotateCw size={14} />
          Rotate 90°
        </button>
      )}
    </div>
  );
};

export default PropertiesPanel;
